import React, { memo, useEffect, useState, useRef, useCallback } from 'react';
import loadable from '@loadable/component';
import classNames from 'classnames';
import ListCard from '../Card/ListCard';
import { updateAlgoliaViews } from '../../../../../util/api';
import css from './Hits.module.css';
import { H4, IconCollection, SkeletonCard } from '../../../../../components';
import LocationBasedPhoneLink from '../../../../../components/LocationBasedPhoneLink/LocationBasedPhoneLink';
import { FormattedMessage, injectIntl } from '../../../../../util/reactIntl';
import { getAlgoliaIndexNameForLang } from '../../../../../util/urlHelpers';
import { useLocationBasedPhoneNumber } from '../../../../../hooks/useLocationBasedPhoneNumber';
import { loadAlgoliaInsights } from '../../../../../analytics/handlers';
import { SUPPORT_EMAIL } from '../../../../../util/supportEmail';
// Lazy-load components that use swiper to avoid SSR issues with ES module
const NoListingsFound = loadable(() =>
  import(/* webpackChunkName: "NoListingsFound" */ '../NoListingFound/NoListingFound')
);
const NoListingFoundSingleCard = loadable(() =>
  import(/* webpackChunkName: "NoListingFoundSingleCard" */ '../NoListingFoundSingleCard/NoListingFoundSingleCard')
);

function insertPromoCards(items, promoCard) {
  const updatedItems = [...items];

  const baseLength = items.length;
  const positions = [6, 13, 20]; // 0-based for 7th, 14th, 21st
  const keyBase = promoCard.key || 'promo';

  // Insert from right to left so indexes remain relative to the original list
  positions
    .filter(pos => baseLength >= pos)
    .sort((a, b) => b - a)
    .forEach(pos => {
      updatedItems.splice(
        pos,
        0,
        React.cloneElement(promoCard, { key: `${keyBase}-p${pos + 1}` })
      );
    });

  return updatedItems;
}

const ALGOLIA_LISTINGS_INDEX = getAlgoliaIndexNameForLang(
  (typeof window !== 'undefined' && localStorage.getItem('appLanguage')) || 'en'
);

const CONTACT_EMAIL = SUPPORT_EMAIL;
const CONTACT_PHONE = process.env.REACT_APP_TEAM_PHONE_NUMBER || '1-833-234-RENT';

const ContactAndMoreOptions = ({ isReleventHits, keyword, area, isMapExploring, intl }) => {
  const displayArea = isMapExploring
    ? intl.formatMessage({ id: "AlgoliaSearchSection.mapArea" }, { defaultMessage: "map area" })
    : area;
  return (
    <div className={classNames(css.lookingCard, isReleventHits.length > 0 && css.releventHits)}>
      <div className={css.textSection}>
        <h2>
          {!!keyword ? (displayArea
            ? <FormattedMessage id="ContactAndMoreOptions.firstHeadingWithArea" values={{ keyword, area: displayArea }} />
            : <FormattedMessage id="ContactAndMoreOptions.firstHeading" values={{ keyword }} />) : (
            <FormattedMessage id="ContactAndMoreOptions.firstHeadingWithoutKeyword" />
          )
          }
        </h2>
        <p>
          <FormattedMessage id="ContactAndMoreOptions.firstParagraph" />
        </p>
      </div>
      <div className={css.buttonSection}>
        <LocationBasedPhoneLink
          className={css.secondaryButton}
          defaultPhoneNumber={CONTACT_PHONE}
        >
          <IconCollection name={"phone_icon"} />
          <FormattedMessage id="ContactAndMoreOptions.callUs" defaultMessage="Call:" />
        </LocationBasedPhoneLink>
        <a href={`mailto:${CONTACT_EMAIL}`} className={css.primaryButton}>
          <IconCollection name={"email_icon"} />
          <FormattedMessage id="ContactAndMoreOptions.contactSupplyTeam" defaultMessage="Contact Our Supply Team" />
        </a>
      </div>
    </div>
  );
};

const MoreContentOptions = () => {
  return (
    <div className={css.moreOptionContent}>
      <h5>
        <FormattedMessage id="ContactAndMoreOptions.secondHeading" />
      </h5>
      <p>
        <FormattedMessage id="ContactAndMoreOptions.secondParagraph" />
      </p>
    </div>
  );
};

const LoadingSpinner = () => (
  <div className={css.loadingSpinner}>
    <div className={css.spinner}></div>
    <FormattedMessage id="CustomHits.loadingMore" defaultMessage="Loading more listings..." />
  </div>
);

const SkeletonGrid = () => (
  <div className={css.rentals} id="hits">
    {Array.from({ length: 12 }, (_, index) => (
      <SkeletonCard
        key={`skeleton-${index}`}
        style={{
          width: 'calc(50% - 12px)',
          height: '400px', // Match approximate card height
          borderRadius: '16px'
        }}
      />
    ))}
  </div>
);

const CustomHits = props => {
  const {
    history,
    currentUser,
    routesConfiguration,
    currentUserCurrency,
    exchangeRates,
    onActivateListing,
    onPrefetchListing,
    algoliaListings,
    queryID,
    algoliaQueryInProgress,
    algoliaBackgroundQueryInProgress,
    algoliaQueryError,
    queryString,
    currentUserCountryCode,
    locale,
    keyword,
    selectedArea,
    hasLocationAvailable,
    isMapExploring,
    intl,
    startDate,
    endDate,
  } = props;

  const [relevantVisibleCount, setRelevantVisibleCount] = useState(12);
  const [irrelevantVisibleCount, setIrrelevantVisibleCount] = useState(12);
  const [isLoadingMoreRelevant, setIsLoadingMoreRelevant] = useState(false);
  const [isLoadingMoreIrrelevant, setIsLoadingMoreIrrelevant] = useState(false);
  const locationBasedPhoneNumber = useLocationBasedPhoneNumber(CONTACT_PHONE);

  const updateViewsTimer = useRef(null);
  const lastSearchLocationKeyRef = useRef('');
  const lastPartialKeywordKeyRef = useRef('');
  const pricesContainerRef = useRef(null);
  const relevantListingsRef = useRef(null);
  const irrelevantListingsRef = useRef(null);
  const observerRelevant = useRef(null);
  const observerIrrelevant = useRef(null);

  const relevantListings = algoliaListings.relevant || [];
  const irrelevantListings = algoliaListings.irrelevant || [];
  const userToken = currentUser?.id?.uuid;

  const productsPerPage = 12;

  const isPartialMatch = (hit) => {
    const highlight = hit?._highlightResult;
    if (!highlight) return false;

    let hasFull = false;
    let hasPartial = false;

    const checkNode = (node) => {
      if (!node) return;
      if (Array.isArray(node)) {
        node.forEach(checkNode);
        return;
      }
      if (typeof node === 'object') {
        if (node.matchLevel) {
          if (node.matchLevel === 'full') hasFull = true;
          if (['partial'].includes(node.matchLevel)) hasPartial = true;
        }
        Object.values(node).forEach(checkNode);
      }
    };

    Object.values(highlight).forEach(checkNode);
    return hasPartial && !hasFull;
  };

  // Reset visible counts when query changes
  useEffect(() => {
    setRelevantVisibleCount(12);
    setIrrelevantVisibleCount(12);
  }, [queryString]);

  const loadMoreRelevant = useCallback(() => {
    if (isLoadingMoreRelevant || (relevantVisibleCount >= relevantListings.length)) return;

    setIsLoadingMoreRelevant(true);

    // Artificial delay to show loading state
    setTimeout(() => {
      setRelevantVisibleCount(prev => Math.min(prev + productsPerPage, relevantListings.length));
      setIsLoadingMoreRelevant(false);
    }, 500);
  }, [isLoadingMoreRelevant, relevantVisibleCount, relevantListings.length]);

  const loadMoreIrrelevant = useCallback(() => {
    if (isLoadingMoreIrrelevant || irrelevantVisibleCount >= irrelevantListings.length) return;

    setIsLoadingMoreIrrelevant(true);

    // Artificial delay to show loading state
    setTimeout(() => {
      setIrrelevantVisibleCount(prev => Math.min(prev + productsPerPage, irrelevantListings.length));
      setIsLoadingMoreIrrelevant(false);
    }, 500);
  }, [isLoadingMoreIrrelevant, irrelevantVisibleCount, irrelevantListings.length]);

  // Set up Intersection Observer for relevant listings with optimized settings
  useEffect(() => {
    if (observerRelevant.current) {
      observerRelevant.current.disconnect();
    }

    if (relevantVisibleCount < relevantListings.length) {
      observerRelevant.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            loadMoreRelevant();
          }
        },
        {
          threshold: 0.1,
          rootMargin: '100px 0px' // Start loading 100px before the sentinel comes into view
        }
      );

      const sentinel = document.getElementById('relevant-sentinel');
      if (sentinel) {
        observerRelevant.current.observe(sentinel);
      }
    }

    return () => {
      if (observerRelevant.current) {
        observerRelevant.current.disconnect();
      }
    };
  }, [relevantVisibleCount, relevantListings.length, loadMoreRelevant]);

  // Set up Intersection Observer for irrelevant listings with optimized settings
  useEffect(() => {
    if (observerIrrelevant.current) {
      observerIrrelevant.current.disconnect();
    }

    if (irrelevantVisibleCount < irrelevantListings.length) {
      observerIrrelevant.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            loadMoreIrrelevant();
          }
        },
        {
          threshold: 0.1,
          rootMargin: '100px 0px' // Start loading 100px before the sentinel comes into view
        }
      );

      const sentinel = document.getElementById('irrelevant-sentinel');
      if (sentinel) {
        observerIrrelevant.current.observe(sentinel);
      }
    }

    return () => {
      if (observerIrrelevant.current) {
        observerIrrelevant.current.disconnect();
      }
    };
  }, [irrelevantVisibleCount, irrelevantListings.length, loadMoreIrrelevant]);

  const renderableRelevantHits = relevantListings.slice(0, relevantVisibleCount);
  const renderableIrrelevantHits = irrelevantListings.slice(0, irrelevantVisibleCount);

  const hitsToRenderLength = relevantListings.length + irrelevantListings.length;

  useEffect(() => {
    if (!userToken) return;
    if (!selectedArea) return;
    if (typeof window === 'undefined') return;

    const normalizedQuery = String(keyword || '').trim();
    const normalizedCity = String(selectedArea).trim();
    if (!normalizedCity) return;

    const queryKey = normalizedQuery ? normalizedQuery.toLowerCase() : 'no-query';
    const searchLocationKey = `${queryKey}|${normalizedCity.toLowerCase()}`;
    if (lastSearchLocationKeyRef.current === searchLocationKey) return;
    lastSearchLocationKeyRef.current = searchLocationKey;

    const needsQuotes = /\\s|:|\"/.test(normalizedCity);
    const escapedCity = normalizedCity.replace(/\"/g, '\\"');
    const cityFilter = `publicData.city:${needsQuotes ? `\"${escapedCity}\"` : escapedCity}`;

    loadAlgoliaInsights().then(aa => {
      if (!aa || typeof aa !== 'function') return;
      aa('viewedFilters', {
        userToken,
        eventName: normalizedQuery
          ? `Search Query + Location: ${normalizedQuery}`
          : 'Search Location Viewed',
        index: ALGOLIA_LISTINGS_INDEX,
        filters: [cityFilter],
      });
    });
  }, [userToken, keyword, selectedArea]);

  useEffect(() => {
    if (updateViewsTimer.current) {
      clearTimeout(updateViewsTimer.current);
    }

    // Need to update the view count for each hit.
    const recordIds = algoliaListings && (relevantListings.length || irrelevantListings.length)
      ? [...relevantListings, ...irrelevantListings].map(h => h.objectID)
      : null;

    if (recordIds) {
      updateViewsTimer.current = setTimeout(() => {
        updateAlgoliaViews({
          indexName: ALGOLIA_LISTINGS_INDEX,
          recordIds,
        }).catch(err => console.log('Error:failed to update views'));
      }, 500);
    }

    return () => {
      if (updateViewsTimer.current) {
        clearTimeout(updateViewsTimer.current);
      }
    };
  }, [hitsToRenderLength]);

  // Search results view + no-results tracking are handled in AlgoliaSearchPage.duck.js

  useEffect(() => {
    if (algoliaQueryInProgress) return;
    if (!userToken) return;
    if (typeof window === 'undefined') return;

    const normalizedQuery = String(keyword || '').trim();
    if (!normalizedQuery) return;

    const queryKey = normalizedQuery.toLowerCase();
    if (lastPartialKeywordKeyRef.current === queryKey) return;

    const partialObjectIDs = [...relevantListings, ...irrelevantListings]
      .filter(isPartialMatch)
      .map(hit => String(hit.objectID))
      .filter(Boolean);

    if (!partialObjectIDs.length) return;
    lastPartialKeywordKeyRef.current = queryKey;

    const CHUNK_SIZE = 20;

    loadAlgoliaInsights().then(aa => {
      if (!aa || typeof aa !== 'function') return;
      for (let i = 0; i < partialObjectIDs.length; i += CHUNK_SIZE) {
        const chunk = partialObjectIDs.slice(i, i + CHUNK_SIZE);
        aa('viewedObjectIDs', {
          userToken,
          eventName: `Partial Keyword Match: ${normalizedQuery}`,
          index: ALGOLIA_LISTINGS_INDEX,
          objectIDs: chunk,
        });
      }
    });
  }, [algoliaQueryInProgress, userToken, keyword, relevantListings, irrelevantListings]);

  const renderListCard = useCallback((item, index) => React.isValidElement(item)
    ? item
    : (
      <ListCard
        rental={item}
        key={item.objectID}
        layout="grid"
        currentUserCurrency={currentUserCurrency}
        exchangeRates={exchangeRates}
        history={history}
        routes={routesConfiguration}
        queryID={item.__queryID || item.queryID || queryID}
        position={item.__position || index + 1}
        onActivateListing={onActivateListing}
        onPrefetchListing={onPrefetchListing}
        currentUserCountryCode={currentUserCountryCode}
        locale={locale}
        hasLocationAvailable={hasLocationAvailable}
        isCritical={[0, 1, 2, 3].includes(index)}
        locationBasedPhoneNumber={locationBasedPhoneNumber}
        titleClassName={css.CardTitle}
        currentUser={currentUser}
        startDate={startDate}
        endDate={endDate}
      />
    ), [
    currentUserCurrency,
    exchangeRates,
    history,
    routesConfiguration,
    queryID,
    onActivateListing,
    onPrefetchListing,
    currentUserCountryCode,
    locale,
    hasLocationAvailable,
    startDate,
    endDate,
  ]);

  // While any fetch — the first fetch, the background "full results" fetch, or
  // their retries — could still return results, keep showing the skeleton rather
  // than an error. A failed first fetch is followed by the background fetch,
  // which usually recovers, so surfacing an error here would just panic users
  // over a blip that is about to resolve itself.
  const isFetchingResults = algoliaQueryInProgress || algoliaBackgroundQueryInProgress;
  if (isFetchingResults && hitsToRenderLength === 0) {
    return <SkeletonGrid />;
  }

  // Foreground refetch (filter / sort change) while previous results are still shown.
  if (algoliaQueryInProgress) {
    return <SkeletonGrid />;
  }

  // Only block the whole results area on error when there is genuinely nothing to
  // show AND every attempt has been exhausted. A stale error must never hide
  // listings that already loaded. The request layer already retried transient
  // failures, so by the time this renders the failure is terminal — state it
  // plainly rather than offer a manual retry that would just repeat it.
  if (algoliaQueryError && hitsToRenderLength === 0) {
    return (
      <H4 className={css.error}>
        <FormattedMessage
          id="CustomHits.loadError"
          defaultMessage="We're having trouble loading search results right now. Please try again in a little while."
        />
      </H4>
    );
  }

  return (
    <>
      {/* Render relevant listings */}
      <div ref={relevantListingsRef}>
        {renderableRelevantHits.length > 0 && (
          <>
            <div className={css.rentals} id="hits">
              {insertPromoCards(
                renderableRelevantHits,
                <NoListingFoundSingleCard keyword={keyword} selectedArea={selectedArea} isMapExploring={isMapExploring} key={`${relevantVisibleCount}-promo-card`} />
              ).map((item, index) => renderListCard(item, index))}
            </div>

            {/* Loading spinner for relevant listings */}
            {isLoadingMoreRelevant && <LoadingSpinner />}

            {/* Sentinel element for infinite scroll */}
            {relevantVisibleCount < relevantListings.length && (
              <div id="relevant-sentinel" style={{ height: '1px' }} />
            )}
          </>
        )}
      </div>

      {/* Show contact and more options if we've shown all relevant listings */}
      {relevantVisibleCount >= relevantListings.length && renderableIrrelevantHits.length > 0 && (
        <div>
          <ContactAndMoreOptions isReleventHits={renderableRelevantHits} keyword={keyword} area={selectedArea} isMapExploring={isMapExploring} intl={intl} />

          {/* {relevantListings.length === 0 && (
            <NoListingsFound keyword={keyword} selectedArea={selectedArea} isMapExploring={isMapExploring} />
          )} */}

          {/* Render irrelevant listings */}
          <div ref={irrelevantListingsRef}>
            <MoreContentOptions />

            {renderableIrrelevantHits.length > 0 && (
              <>
                <div className={css.rentals} id="hits">
                  {insertPromoCards(
                    renderableIrrelevantHits,
                    <>
                      <NoListingsFound keyword={keyword} selectedArea={selectedArea} isMapExploring={isMapExploring} />
                      <NoListingFoundSingleCard keyword={keyword} selectedArea={selectedArea} isMapExploring={isMapExploring} key={`${irrelevantVisibleCount}-promo-card`} />
                    </>
                  ).map((item, index) => renderListCard(item, index))}
                </div>

                {/* Loading spinner for irrelevant listings */}
                {isLoadingMoreIrrelevant && <LoadingSpinner />}

                {/* Sentinel element for infinite scroll */}
                {irrelevantVisibleCount < irrelevantListings.length && (
                  <div id="irrelevant-sentinel" style={{ height: '1px' }} />
                )}
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default memo(injectIntl(CustomHits));
